import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import { TableActionColumnHeader } from "../components/TableShell";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import DuplicateSuccessMessage from "../components/DuplicateSuccessMessage";
import DetailSaveStatus from "../components/DetailSaveStatus";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { attachValidatedCsvFileImport, downloadCsvTextFile } from "../shared/utils/csvFileHelpers";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import InlinePickInput from "../components/InlinePickInput";
import useBlurAutoSave from "../shared/hooks/useBlurAutoSave";
import uiConfig from "../uiConfig";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { buildDuplicateLabel } from "../shared/utils/duplicateLabel";
import LinkifiedEditableRead from "../components/LinkifiedEditableRead";
import AssetEntityCard from "../components/AssetEntityCard";
import EntityLabelFilterBar from "../components/entity/EntityLabelFilterBar";
import { useEntityLabelFilter } from "../shared/hooks/useEntityLabelFilter";
import { IconEdit, IconDuplicate, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import AssetManagementSectionTabs from "../components/AssetManagementSectionTabs";
import {
  ASSET_TAB_ASSETS,
  ASSET_TAB_INSURANCE,
  ASSET_TAB_VENDORS,
  ASSET_TAB_RENTAL_LEASES,
  ASSET_TAB_RENT_LEDGER,
} from "../shared/utils/assetManagementTabs";
import RentalLease from "./RentalLease";
import RentLedger from "./RentLedger";
import InsuranceManagement from "./InsuranceManagement";
import Vendor from "./Vendor";

import { apiFetch } from "../shared/api/apiFetch";

function AssetManagement() {
  const [searchParams] = useSearchParams();
  const tabRaw = (searchParams.get("tab") || "").trim().toLowerCase();
  const activeTab =
    tabRaw === ASSET_TAB_INSURANCE || tabRaw === "insurance_management" || tabRaw === "insurance-management"
      ? ASSET_TAB_INSURANCE
      : tabRaw === ASSET_TAB_VENDORS || tabRaw === "vendor"
        ? ASSET_TAB_VENDORS
        : tabRaw === ASSET_TAB_RENTAL_LEASES || tabRaw === "rental-leases" || tabRaw === "rental_leases"
          ? ASSET_TAB_RENTAL_LEASES
          : tabRaw === ASSET_TAB_RENT_LEDGER || tabRaw === "rent-ledger" || tabRaw === "rent_ledger"
            ? ASSET_TAB_RENT_LEDGER
            : ASSET_TAB_ASSETS;

  if (activeTab === ASSET_TAB_INSURANCE) return <InsuranceManagement />;
  if (activeTab === ASSET_TAB_VENDORS) return <Vendor />;
  if (activeTab === ASSET_TAB_RENTAL_LEASES) return <RentalLease />;
  if (activeTab === ASSET_TAB_RENT_LEDGER) return <RentLedger />;

  return <AssetManagementAssetsView />;
}

function AssetManagementAssetsView() {
  const [currentObjs, setCurrentObjs] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    importing,
    setImporting,
    importPanelExpanded,
    setImportPanelExpanded,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialSort: { key: null, direction: "asc" }, initialViewMode: "card" });
  const {
    activeLabel: activeCategory,
    setActiveLabel: setActiveCategory,
    labelOptions: categoryOptions,
    filteredItems: categoryFilteredObjs,
    labelsForNewItem: categoriesForNewItem,
  } = useEntityLabelFilter({ items: currentObjs, labelField: "category" });

  // Detail modal state
  const [detailMode, setDetailMode] = useState(""); // '', 'view', 'edit', 'new'
  const [detailAssetId, setDetailAssetId] = useState(null);
  const [detailName, setDetailName] = useState("");
  const [detailCategory, setDetailCategory] = useState("");
  const [detailAddress, setDetailAddress] = useState("");
  const [detailNotes, setDetailNotes] = useState("");
  const [detailActiveField, setDetailActiveField] = useState("");
  const [detailInitialValues, setDetailInitialValues] = useState(null);

  useEffect(() => {
    getDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  function getDocument() {
    if (!account_id) return;
    apiFetch(serverUrl + "/listAssets", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function getFilteredAndSortedData() {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    let filtered = (categoryFilteredObjs || []).filter((obj) => {
      const matchesSearch =
        !searchLower ||
        (obj.name && String(obj.name).toLowerCase().includes(searchLower)) ||
        (obj.category && String(obj.category).toLowerCase().includes(searchLower)) ||
        (obj.address && String(obj.address).toLowerCase().includes(searchLower)) ||
        (obj.notes && String(obj.notes).toLowerCase().includes(searchLower));
      return matchesSearch;
    });
    return sortRowsByConfig(filtered, sortConfig);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  function deleteDocument(currentObj) {
    const { _id: id } = currentObj;
    apiFetch(serverUrl + "/deleteAsset", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: id }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Asset", message: data, mode: "alert", onConfirm: null });
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(id);
        setSelectedIds(newSelectedIds);
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function duplicateDocument(currentObj) {
    const duplicateName = buildDuplicateLabel(currentObj?.name, { fallback: "Asset" });
    apiFetch(serverUrl + "/createAsset", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        name: duplicateName,
        category: (currentObj?.category != null ? String(currentObj.category) : "").trim(),
        notes: (currentObj?.notes != null ? String(currentObj.notes) : "").trim(),
        address: (currentObj?.address != null ? String(currentObj.address) : "").trim(),
      }),
    })
      .then(async (response) => {
        const data = await response.text();
        if (!response.ok) throw new Error(data || "Failed to duplicate asset");
        let created = null;
        try {
          created = JSON.parse(data);
        } catch {
          created = null;
        }
        const openCreated = () => {
          closeConfirmDialog();
          if (created && created._id) {
            openAssetDetail(
              {
                _id: created._id,
                name: created.name != null ? String(created.name) : duplicateName,
                category: created.category != null ? String(created.category) : "",
                address: created.address != null ? String(created.address) : "",
                notes: created.notes != null ? String(created.notes) : "",
              },
              "edit"
            );
          }
        };
        setConfirmDialog({
          open: true,
          title: "Asset duplicated",
          message:
            created && created._id ? (
              <DuplicateSuccessMessage linkLabel={duplicateName.trim() || "Asset"} onOpenDetail={openCreated} footer={null} />
            ) : (
              `Created "${duplicateName}".`
            ),
          onConfirm: null,
          mode: "alert",
        });
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete assets", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete assets",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteAsset", {
              method: "POST",
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        getDocument();
        setDeleting(false);
      },
    });
  }

  function getDetailFormValues(asset) {
    return {
      name: asset?.name || "",
      category: asset?.category || "",
      address: asset?.address != null ? String(asset.address) : "",
      notes: asset?.notes != null ? String(asset.notes) : "",
    };
  }

  function applyDetailFormValues(values) {
    setDetailName(values?.name || "");
    setDetailCategory(values?.category || "");
    setDetailAddress(values?.address != null ? String(values.address) : "");
    setDetailNotes(values?.notes != null ? String(values.notes) : "");
  }

  function openAssetDetail(asset, mode = "view") {
    setDetailMode(mode);
    if (!asset && mode === "new") {
      setDetailAssetId(null);
      const initialValues = getDetailFormValues(null);
      const categoryFromFilter = Array.isArray(categoriesForNewItem) ? categoriesForNewItem[0] : "";
      if (categoryFromFilter) initialValues.category = categoryFromFilter;
      setDetailInitialValues(initialValues);
      applyDetailFormValues(initialValues);
      setDetailActiveField("name");
      return;
    }
    if (!asset) {
      setDetailMode("");
      return;
    }
    setDetailAssetId(asset._id);
    const initialValues = getDetailFormValues(asset);
    setDetailInitialValues(initialValues);
    applyDetailFormValues(initialValues);
    setDetailActiveField(mode === "edit" ? "name" : "");
  }

  async function persistDetailChanges() {
    const nameVal = (detailName || "").trim();
    if (!nameVal) return;
    const payload = {
      account_id,
      name: nameVal,
      category: (detailCategory || "").trim(),
      address: (detailAddress || "").trim(),
      notes: (detailNotes || "").trim(),
    };
    const isNew = detailMode === "new" || !detailAssetId;
    const url = isNew ? serverUrl + "/createAsset" : serverUrl + "/updateAsset";
    const body = isNew ? payload : { ...payload, _id: detailAssetId };

    try {
      if (isNew) {
        const resp = await apiFetch(url, {
          method: "POST",
          body: JSON.stringify(body),
        });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          setBannerError((data && data.message) || "Request failed.");
          return;
        }
        if (data && data._id) {
          setDetailAssetId(data._id);
          setDetailMode("edit");
        }
      } else {
        const resp = await apiFetch(url, {
          method: "POST",
          body: JSON.stringify(body),
        });
        const text = await resp.text();
        if (!resp.ok) {
          setBannerError(text || "Request failed.");
          return;
        }
      }
      await getDocument();
      setDetailInitialValues({
        name: nameVal,
        category: (detailCategory || "").trim(),
        address: (detailAddress || "").trim(),
        notes: (detailNotes || "").trim(),
      });
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  function parseCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const nameIdx = findCsvColumnIndex(headers, ["name", "asset", "asset_name"]);
    const categoryIdx = findCsvColumnIndex(headers, ["category", "type"]);
    const addressIdx = findCsvColumnIndex(headers, ["address"]);
    const notesIdx = findCsvColumnIndex(headers, ["notes"]);
    if (nameIdx === -1) {
      throw new Error("CSV must contain a name (or asset) column");
    }
    const records = [];
    for (const values of rows) {
      records.push({
        name: values[nameIdx] ?? "",
        category: categoryIdx >= 0 && values[categoryIdx] !== undefined ? values[categoryIdx] : "",
        address: addressIdx >= 0 && values[addressIdx] !== undefined ? values[addressIdx] : "",
        notes: notesIdx >= 0 && values[notesIdx] !== undefined ? values[notesIdx] : "",
      });
    }
    return records;
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseCSV(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        if (!(record.name && String(record.name).trim())) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing name`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + "/createAsset", {
            method: "POST",
            body: JSON.stringify({
              account_id,
              name: String(record.name).trim(),
              category: (record.category != null ? String(record.category) : "").trim(),
              address: (record.address != null ? String(record.address) : "").trim(),
              notes: (record.notes != null ? String(record.notes) : "").trim(),
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(error)}`);
        }
      }
      setConfirmDialog({
        open: true,
        title: "Import completed",
        message: formatBatchSummaryMessage("Import completed!", successCount, errorCount, errors),
        onConfirm: null,
        mode: "alert",
      });
      getDocument();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, "Error importing CSV."));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile("asset-sample.csv", [
      "name,category,address,notes",
      'Adobe,Investment Home,"903699 Fox Hollow Trail, Irving, TX 75063",HVAC filters in hall closet',
      "Generator,Equipment,,Backup generator for the main building",
      "Projector,AV,,Used for presentations in conference room",
    ]);
  }

  const detailCurrentValues = {
    name: (detailName || "").trim(),
    category: (detailCategory || "").trim(),
    address: (detailAddress || "").trim(),
    notes: (detailNotes || "").trim(),
  };
  const detailHasChanges =
    !!detailInitialValues &&
    Object.keys(detailCurrentValues).some((k) => detailCurrentValues[k] !== detailInitialValues[k]);
  const { isSaving: detailSaving, handleFieldBlur: handleDetailFieldBlur, handleFieldCommitKeyDown: handleDetailFieldCommitKeyDown } = useBlurAutoSave({
    hasChanges: detailHasChanges,
    saveFn: persistDetailChanges,
    onExitEdit: () => setDetailActiveField(""),
  });

  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <EntityPageScaffold
      title="Asset Management"
      titleTabs={<AssetManagementSectionTabs activeTab={ASSET_TAB_ASSETS} />}
      subtitle="Manage assets, categories, address, and notes."
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          {(detailMode === "view" || detailMode === "edit" || detailMode === "new") && (
            <div className="detail-modal-overlay">
              <div
                role="button"
                tabIndex={0}
                className="detail-modal-backdrop"
                onClick={() => setDetailMode("")}
                onKeyDown={(e) => e.key === "Escape" && setDetailMode("")}
                aria-label="Close"
              />
              <div className="panel detail-modal-panel">
                <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #dee2e6", padding: "12px 16px" }}>
                  <h3 className="panel-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                    {detailMode === "new" ? "New asset" : "Asset details"}
                  </h3>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <DetailSaveStatus isSaving={detailSaving} hasChanges={detailHasChanges} />
                    <button
                      type="button"
                      className="btn-link btn-link-muted"
                      onClick={() => setDetailMode("")}
                      style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div style={{ padding: "12px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Name</label>
                      {detailMode === "new" || detailActiveField === "name" ? (
                        <input
                          autoFocus={detailMode === "new" || detailActiveField === "name"}
                          type="text"
                          value={detailName}
                          onChange={(e) => setDetailName(e.target.value)}
                          onBlur={handleDetailFieldBlur}
                          onKeyDown={handleDetailFieldCommitKeyDown}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("name")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailName || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Category</label>
                      {detailMode === "new" || detailActiveField === "category" ? (
                        <InlinePickInput
                          autoFocus={detailActiveField === "category"}
                          value={detailCategory}
                          onChange={setDetailCategory}
                          onBlur={handleDetailFieldBlur}
                          onKeyDown={handleDetailFieldCommitKeyDown}
                          onOptionSelect={handleDetailFieldBlur}
                          options={categoryOptions}
                          placeholder="Enter or pick category"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("category")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailCategory || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Address</label>
                      {detailMode === "new" || detailActiveField === "address" ? (
                        <textarea
                          autoFocus={detailActiveField === "address"}
                          value={detailAddress}
                          onChange={(e) => setDetailAddress(e.target.value)}
                          onBlur={handleDetailFieldBlur}
                          onKeyDown={handleDetailFieldCommitKeyDown}
                          rows={2}
                          className="input input-full"
                          placeholder="Street, city, state, ZIP"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDetailActiveField("address")}
                          className="btn-link"
                          style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", minHeight: 40, whiteSpace: "pre-wrap", fontWeight: 500 }}
                        >
                          {detailAddress || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Notes</label>
                      {detailMode === "new" || detailActiveField === "notes" ? (
                        <textarea
                          autoFocus={detailActiveField === "notes"}
                          value={detailNotes}
                          onChange={(e) => setDetailNotes(e.target.value)}
                          onBlur={handleDetailFieldBlur}
                          onKeyDown={handleDetailFieldCommitKeyDown}
                          rows={3}
                          className="input input-full"
                        />
                      ) : (
                        <LinkifiedEditableRead
                          text={detailNotes}
                          onBeginEdit={() => setDetailActiveField("notes")}
                          style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", minHeight: 48, whiteSpace: "pre-wrap" }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ImportCsvPanel
            expanded={importPanelExpanded}
            fileInputId="asset-csv-import"
            importing={importing}
            columnHint="Columns: name (required), category, address, notes"
            onDownloadSample={downloadSampleCSV}
            onFileChange={handleFileChange}
            onImportText={importFromCsvText}
          />

          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search name, category, address, notes…"
            onClearSearch={() => setSearchTerm("")}
            importPanelExpanded={importPanelExpanded}
            onToggleImport={() => setImportPanelExpanded(!importPanelExpanded)}
            onNew={() => {
              openAssetDetail(null, "new");
              setImportPanelExpanded(false);
            }}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          <EntityLabelFilterBar
            items={currentObjs}
            labelField="category"
            activeLabel={activeCategory}
            onActiveLabelChange={setActiveCategory}
            labelOptions={categoryOptions}
            ariaLabel="Asset category"
          />
        </>
      }
      list={
        <div className="table-scroll">
          {viewMode === "table" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        checked={filteredRows.length > 0 && filteredRows.every((obj) => selectedIds.has(obj._id))}
                        onChange={(e) => handleSelectAll(e, getFilteredAndSortedData)}
                        disabled={filteredRows.length === 0}
                      />
                    </th>
                    <th className="sortable" onClick={() => handleSort("name")}>Name</th>
                    <th className="sortable" onClick={() => handleSort("category")}>Category</th>
                    <th className="sortable" onClick={() => handleSort("address")}>Address</th>
                    <th>Notes</th>
                    <TableActionColumnHeader />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((a) => (
                    <tr key={a._id}>
                      <td className="text-center">
                        <input type="checkbox" checked={selectedIds.has(a._id)} onChange={(e) => handleSelectOne(a._id, e.target.checked)} />
                      </td>
                      <td>
                        <button type="button" className="btn-link" onClick={() => openAssetDetail(a, "view")}>
                          {a.name || "—"}
                        </button>
                      </td>
                      <td>{a.category || "—"}</td>
                      <td className="table-cell-notes">{a.address ? a.address : "—"}</td>
                      <td className="table-cell-notes">{a.notes ? a.notes : "—"}</td>
                      <td className="text-center">
                        <RowActionIcons>
                          <button type="button" onClick={() => openAssetDetail(a, "edit")} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                          <button type="button" onClick={() => duplicateDocument(a)} className="btn-link" style={iconBtnStyle} title="Duplicate"><IconDuplicate size={18} /></button>
                          <button type="button" onClick={() => deleteDocument(a)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "card" && (
            <div className="cards-wrapper">
              {filteredRows.map((a) => (
                <AssetEntityCard
                  key={a._id}
                  asset={a}
                  selected={selectedIds.has(a._id)}
                  onSelectChange={(checked) => handleSelectOne(a._id, checked)}
                  onTitleClick={() => openAssetDetail(a, "view")}
                  onEdit={() => openAssetDetail(a, "edit")}
                  onDuplicate={() => duplicateDocument(a)}
                  onDelete={() => deleteDocument(a)}
                />
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}

export default AssetManagement;

